import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import {
  Storefront, Plus, Trash, PencilSimple, X, FloppyDisk, Phone, Globe,
  Star, CheckCircle, CurrencyInr, WhatsappLogo, Link as LinkIcon, MapPin,
} from '@phosphor-icons/react';

const STATUS_COLORS = {
  booked: '#3B82F6',
  confirmed: '#22C55E',
  paid: '#A855F7',
  cancelled: '#EF4444',
};

const formatINR = (n) => '₹' + (Number(n || 0)).toLocaleString('en-IN', { maximumFractionDigits: 0 });

export default function VendorsTab({ event }) {
  const { api } = useAuth();
  const [data, setData] = useState(null);
  const [allVendors, setAllVendors] = useState([]);
  const [categories, setCategories] = useState([]);
  const [showLink, setShowLink] = useState(false);
  const [editingLink, setEditingLink] = useState(null);

  const load = useCallback(async () => {
    try {
      const [r, g] = await Promise.all([
        api.get(`/events/${event.event_id}/vendors`),
        api.get('/vendors'),
      ]);
      setData(r.data);
      setAllVendors(g.data.vendors || []);
      setCategories(g.data.categories || []);
    } catch (e) { console.error(e); }
  }, [api, event.event_id]);

  useEffect(() => { load(); }, [load]);

  const handleUnlink = async (linkId) => {
    if (!window.confirm('Unlink this vendor from the event?')) return;
    await api.delete(`/events/${event.event_id}/vendors/${linkId}`);
    load();
  };

  if (!data) return <Spinner />;
  const { totals, links } = data;

  return (
    <div className="space-y-3" data-testid="vendors-section">
      <div className="glass-card p-4 flex items-center gap-3 flex-wrap">
        <div className="flex-1 min-w-[180px]">
          <h3 className="font-heading text-sm font-bold">Vendors</h3>
          <p className="text-xs text-neutral-400 mt-1">
            {data.count} linked · {formatINR(totals.paid)} paid · {totals.balance > 0 ? <span className="text-yellow-400">{formatINR(totals.balance)} balance due</span> : <span className="text-green-400">fully paid</span>}
          </p>
        </div>
        <div className="text-right">
          <div className="text-[10px] uppercase tracking-wider text-neutral-500">Total Agreed</div>
          <div className="text-base font-bold text-white">{formatINR(totals.agreed)}</div>
        </div>
        <button onClick={() => setShowLink(true)} data-testid="vendor-link-btn"
          className="px-4 py-2 rounded-full bg-[#FF6B35] text-white text-xs font-semibold flex items-center gap-1.5 hover:opacity-90">
          <Plus size={14} weight="bold" /> Link Vendor
        </button>
      </div>

      {showLink && (
        <LinkVendorForm event={event} vendors={allVendors} categories={categories}
          onCancel={() => setShowLink(false)}
          onSaved={() => { setShowLink(false); load(); }} />
      )}

      {editingLink && (
        <EditLinkForm event={event} link={editingLink}
          onCancel={() => setEditingLink(null)}
          onSaved={() => { setEditingLink(null); load(); }} />
      )}

      {links.length === 0 ? (
        <div className="glass-card p-8 text-center" data-testid="vendors-empty">
          <Storefront size={32} className="mx-auto text-neutral-500 mb-2" />
          <p className="text-sm text-neutral-400">No vendors linked yet. Click "Link Vendor" to add caterer, photographer, decorator, etc.</p>
        </div>
      ) : (
        <div className="space-y-2" data-testid="vendor-list">
          {links.map(lk => (
            <VendorRow key={lk.link_id} link={lk} event={event}
              onEdit={() => setEditingLink(lk)}
              onUnlink={() => handleUnlink(lk.link_id)} />
          ))}
        </div>
      )}
    </div>
  );
}

function VendorRow({ link, event, onEdit, onUnlink }) {
  const v = link.vendor || {};
  const statusColor = STATUS_COLORS[link.status] || '#64748B';
  const phoneClean = (v.phone || '').replace(/[^\d+]/g, '');
  const waMsg = `Hi ${v.name}, regarding ${event.title}: ${link.role || ''}.`;
  const waUrl = phoneClean ? `https://wa.me/${phoneClean}?text=${encodeURIComponent(waMsg)}` : null;

  return (
    <div className="glass-card p-4 flex items-start gap-3 flex-wrap" data-testid={`vendor-${link.link_id}`}>
      <div className="w-10 h-10 rounded-xl bg-[#FF6B35]/15 flex items-center justify-center shrink-0">
        <Storefront size={18} className="text-[#FF6B35]" />
      </div>
      <div className="flex-1 min-w-[180px]">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-sm font-bold text-white">{v.name}</span>
          {v.rating > 0 && (
            <span className="text-[10px] text-yellow-400 flex items-center gap-0.5">
              {[...Array(v.rating)].map((_, i) => <Star key={i} size={10} weight="fill" />)}
            </span>
          )}
          <span className="text-[10px] px-1.5 py-0.5 rounded-full font-bold uppercase tracking-wider"
            style={{ background: `${statusColor}18`, color: statusColor }}>
            {link.status}
          </span>
        </div>
        <div className="flex items-center gap-2 mt-0.5 flex-wrap text-[11px] text-neutral-400">
          <span>{v.category}</span>
          {link.role && <><span>·</span><span className="italic">{link.role}</span></>}
          {v.phone && <><span>·</span><Phone size={9} className="inline" /><span>{v.phone}</span></>}
        </div>
        {link.notes && <p className="text-[11px] text-neutral-500 mt-1">{link.notes}</p>}
      </div>
      <div className="text-right">
        <div className="text-[10px] uppercase tracking-wider text-neutral-500">Agreed</div>
        <div className="text-sm font-bold text-white">{formatINR(link.agreed_amount)}</div>
        <div className="text-[11px] mt-0.5">
          <span className="text-green-400">{formatINR(link.advance_paid)} paid</span>
          {link.balance_due > 0 && <span className="text-yellow-400"> · {formatINR(link.balance_due)} due</span>}
        </div>
      </div>
      <div className="flex items-center gap-1">
        {waUrl && (
          <a href={waUrl} target="_blank" rel="noopener noreferrer"
            data-testid={`vendor-wa-${link.link_id}`}
            className="p-1.5 rounded-lg border border-[#25D366]/30 text-[#25D366] hover:bg-[#25D366]/10">
            <WhatsappLogo size={12} />
          </a>
        )}
        <button onClick={onEdit} data-testid={`vendor-edit-${link.link_id}`}
          className="p-1.5 rounded-lg border border-white/10 hover:bg-white/5 text-neutral-400 hover:text-white">
          <PencilSimple size={12} />
        </button>
        <button onClick={onUnlink} data-testid={`vendor-unlink-${link.link_id}`}
          className="p-1.5 rounded-lg border border-red-500/30 text-red-400 hover:bg-red-500/10">
          <Trash size={12} />
        </button>
      </div>
    </div>
  );
}

function LinkVendorForm({ event, vendors, categories, onCancel, onSaved }) {
  const { api } = useAuth();
  const [mode, setMode] = useState('existing'); // 'existing' | 'new'
  const [vendorId, setVendorId] = useState(vendors[0]?.vendor_id || '');
  const [newV, setNewV] = useState({ name: '', category: 'Caterer', phone: '', rating: 0, notes: '' });
  const [link, setLink] = useState({ role: '', agreed_amount: '', advance_paid: '', status: 'booked', notes: '' });
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        role: link.role,
        agreed_amount: parseFloat(link.agreed_amount) || 0,
        advance_paid: parseFloat(link.advance_paid) || 0,
        status: link.status,
        notes: link.notes,
      };
      if (mode === 'existing') {
        if (!vendorId) { alert('Pick a vendor first'); setSaving(false); return; }
        payload.vendor_id = vendorId;
      } else {
        if (!newV.name.trim()) { alert('Vendor name required'); setSaving(false); return; }
        payload.new_vendor = newV;
      }
      await api.post(`/events/${event.event_id}/vendors/link`, payload);
      onSaved();
    } catch (e) { alert('Failed: ' + (e.response?.data?.detail || e.message)); }
    setSaving(false);
  };

  return (
    <div className="glass-card p-4 border border-[#FF6B35]/30 space-y-3" data-testid="vendor-link-form">
      <div className="flex gap-1 p-1 rounded-lg bg-white/5 w-fit">
        <button type="button" onClick={() => setMode('existing')} data-testid="link-mode-existing"
          className={`px-3 py-1 rounded text-[11px] font-bold ${mode === 'existing' ? 'bg-[#FF6B35] text-white' : 'text-neutral-400'}`}>
          Pick existing
        </button>
        <button type="button" onClick={() => setMode('new')} data-testid="link-mode-new"
          className={`px-3 py-1 rounded text-[11px] font-bold ${mode === 'new' ? 'bg-[#FF6B35] text-white' : 'text-neutral-400'}`}>
          + Add new
        </button>
      </div>

      {mode === 'existing' ? (
        <Field label="Vendor">
          <select value={vendorId} onChange={(e) => setVendorId(e.target.value)} data-testid="link-vendor-pick"
            className="w-full px-3 py-2 rounded-lg bg-white/5 border border-white/10 text-sm text-white">
            <option value="" className="bg-neutral-900">— Select —</option>
            {vendors.map(v => (
              <option key={v.vendor_id} value={v.vendor_id} className="bg-neutral-900">
                {v.name} ({v.category}){v.rating > 0 ? ` ★${v.rating}` : ''}
              </option>
            ))}
          </select>
        </Field>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
          <Field label="Name *">
            <input value={newV.name} onChange={(e) => setNewV(v => ({ ...v, name: e.target.value }))}
              data-testid="link-new-name" className="w-full px-3 py-2 rounded-lg bg-white/5 border border-white/10 text-sm text-white" />
          </Field>
          <Field label="Category">
            <select value={newV.category} onChange={(e) => setNewV(v => ({ ...v, category: e.target.value }))}
              data-testid="link-new-category" className="w-full px-3 py-2 rounded-lg bg-white/5 border border-white/10 text-sm text-white">
              {categories.map(c => <option key={c} value={c} className="bg-neutral-900">{c}</option>)}
            </select>
          </Field>
          <Field label="Phone">
            <input value={newV.phone} onChange={(e) => setNewV(v => ({ ...v, phone: e.target.value }))}
              data-testid="link-new-phone" placeholder="+91 98..."
              className="w-full px-3 py-2 rounded-lg bg-white/5 border border-white/10 text-sm text-white" />
          </Field>
          <Field label="Rating (0-5)">
            <input type="number" min="0" max="5" value={newV.rating} onChange={(e) => setNewV(v => ({ ...v, rating: parseInt(e.target.value, 10) || 0 }))}
              data-testid="link-new-rating" className="w-full px-3 py-2 rounded-lg bg-white/5 border border-white/10 text-sm text-white" />
          </Field>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
        <Field label="Role on this event">
          <input value={link.role} onChange={(e) => setLink(l => ({ ...l, role: e.target.value }))}
            data-testid="link-role" placeholder="e.g. Main caterer"
            className="w-full px-3 py-2 rounded-lg bg-white/5 border border-white/10 text-sm text-white" />
        </Field>
        <Field label="Agreed (₹)">
          <input type="number" value={link.agreed_amount} onChange={(e) => setLink(l => ({ ...l, agreed_amount: e.target.value }))}
            data-testid="link-agreed" className="w-full px-3 py-2 rounded-lg bg-white/5 border border-white/10 text-sm text-white" />
        </Field>
        <Field label="Advance paid (₹)">
          <input type="number" value={link.advance_paid} onChange={(e) => setLink(l => ({ ...l, advance_paid: e.target.value }))}
            data-testid="link-advance" className="w-full px-3 py-2 rounded-lg bg-white/5 border border-white/10 text-sm text-white" />
        </Field>
        <Field label="Status">
          <select value={link.status} onChange={(e) => setLink(l => ({ ...l, status: e.target.value }))}
            data-testid="link-status" className="w-full px-3 py-2 rounded-lg bg-white/5 border border-white/10 text-sm text-white">
            <option value="booked" className="bg-neutral-900">Booked</option>
            <option value="confirmed" className="bg-neutral-900">Confirmed</option>
            <option value="paid" className="bg-neutral-900">Paid in full</option>
            <option value="cancelled" className="bg-neutral-900">Cancelled</option>
          </select>
        </Field>
      </div>

      <Field label="Notes (contract terms, special arrangements)">
        <input value={link.notes} onChange={(e) => setLink(l => ({ ...l, notes: e.target.value }))}
          data-testid="link-notes" className="w-full px-3 py-2 rounded-lg bg-white/5 border border-white/10 text-sm text-white" />
      </Field>

      <div className="flex gap-2 justify-end">
        <button onClick={onCancel} className="px-4 py-2 rounded-full border border-white/10 text-xs font-medium hover:bg-white/5">Cancel</button>
        <button onClick={save} disabled={saving} data-testid="link-save-btn"
          className="px-4 py-2 rounded-full bg-[#FF6B35] text-white text-xs font-semibold flex items-center gap-1.5 disabled:opacity-50">
          <FloppyDisk size={14} /> {saving ? 'Saving...' : 'Link Vendor'}
        </button>
      </div>
    </div>
  );
}

function EditLinkForm({ event, link, onCancel, onSaved }) {
  const { api } = useAuth();
  const [form, setForm] = useState({
    role: link.role || '',
    agreed_amount: link.agreed_amount ?? '',
    advance_paid: link.advance_paid ?? '',
    status: link.status || 'booked',
    notes: link.notes || '',
  });
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/events/${event.event_id}/vendors/${link.link_id}`, {
        ...form,
        agreed_amount: parseFloat(form.agreed_amount) || 0,
        advance_paid: parseFloat(form.advance_paid) || 0,
      });
      onSaved();
    } catch (e) { alert('Save failed: ' + (e.response?.data?.detail || e.message)); }
    setSaving(false);
  };

  return (
    <div className="glass-card p-4 border border-blue-500/30 space-y-3" data-testid="vendor-edit-form">
      <h4 className="text-xs font-bold">Edit {link.vendor?.name}</h4>
      <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
        <Field label="Role">
          <input value={form.role} onChange={(e) => setForm(f => ({ ...f, role: e.target.value }))}
            className="w-full px-3 py-2 rounded-lg bg-white/5 border border-white/10 text-sm text-white" />
        </Field>
        <Field label="Agreed (₹)">
          <input type="number" value={form.agreed_amount} onChange={(e) => setForm(f => ({ ...f, agreed_amount: e.target.value }))}
            className="w-full px-3 py-2 rounded-lg bg-white/5 border border-white/10 text-sm text-white" />
        </Field>
        <Field label="Paid so far (₹)">
          <input type="number" value={form.advance_paid} onChange={(e) => setForm(f => ({ ...f, advance_paid: e.target.value }))}
            className="w-full px-3 py-2 rounded-lg bg-white/5 border border-white/10 text-sm text-white" />
        </Field>
        <Field label="Status">
          <select value={form.status} onChange={(e) => setForm(f => ({ ...f, status: e.target.value }))}
            className="w-full px-3 py-2 rounded-lg bg-white/5 border border-white/10 text-sm text-white">
            <option value="booked" className="bg-neutral-900">Booked</option>
            <option value="confirmed" className="bg-neutral-900">Confirmed</option>
            <option value="paid" className="bg-neutral-900">Paid in full</option>
            <option value="cancelled" className="bg-neutral-900">Cancelled</option>
          </select>
        </Field>
      </div>
      <Field label="Notes">
        <input value={form.notes} onChange={(e) => setForm(f => ({ ...f, notes: e.target.value }))}
          className="w-full px-3 py-2 rounded-lg bg-white/5 border border-white/10 text-sm text-white" />
      </Field>
      <div className="flex gap-2 justify-end">
        <button onClick={onCancel} className="px-4 py-2 rounded-full border border-white/10 text-xs font-medium hover:bg-white/5">Cancel</button>
        <button onClick={save} disabled={saving}
          className="px-4 py-2 rounded-full bg-[#FF6B35] text-white text-xs font-semibold flex items-center gap-1.5 disabled:opacity-50">
          <FloppyDisk size={14} /> {saving ? 'Saving...' : 'Save'}
        </button>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="text-[10px] uppercase tracking-wider text-neutral-500 block mb-1">{label}</span>
      {children}
    </label>
  );
}

function Spinner() {
  return <div className="flex justify-center py-12"><div className="w-6 h-6 border-2 border-[#FF6B35] border-t-transparent rounded-full animate-spin" /></div>;
}
